import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import firestoreService from '../services/firestoreService';
import AdminHeader from '../components/admin/AdminHeader';
import AdminNavigation from '../components/admin/AdminNavigation';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        totalAuditLogs: 0,
        todayLogs: 0
    });
    const [recentLogs, setRecentLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Check authentication
        const isAuthenticated = localStorage.getItem('adminAuth');
        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        // Simulate loading dashboard data
        loadDashboardData();
    }, [navigate]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);

            // Get analytics data from Firestore
            const analyticsData = await firestoreService.getAnalyticsData('7d');

            setStats({
                totalUsers: analyticsData.userStats?.totalUsers || 0,
                activeUsers: analyticsData.userStats?.activeUsers || 0,
                totalAuditLogs: analyticsData.auditStats?.totalLogs || 0,
                todayLogs: analyticsData.auditStats?.todayLogs || 0
            });

            // Get recent audit logs
            const recentLogsData = await firestoreService.getAuditLogs({ limit: 10 });
            setRecentLogs(recentLogsData);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            // Fallback to default values if Firestore fails
            setStats({
                totalUsers: 0,
                activeUsers: 0,
                totalAuditLogs: 0,
                todayLogs: 0
            });
            setRecentLogs([]);
            // Don't show alert - just silently fail
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminAuth');
        localStorage.removeItem('adminUser');
        navigate('/admin');
    };

    const formatTimestamp = (timestamp) => {
        if (timestamp && timestamp.toDate) {
            return timestamp.toDate().toLocaleString();
        }
        return new Date(timestamp).toLocaleString();
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'LOW': return '#28a745';
            case 'MEDIUM': return '#ffc107';
            case 'HIGH': return '#dc3545';
            case 'CRITICAL': return '#6f42c1';
            default: return '#6c757d';
        }
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <AdminHeader title="Zinea Admin Dashboard">
                <button
                    onClick={loadDashboardData}
                    className="refresh-button"
                    disabled={loading}
                >
                    {loading ? '⏳' : '🔄'} Refresh
                </button>
            </AdminHeader>

            <AdminNavigation />

            <main className="admin-main">
                <div className="dashboard-stats">
                    <div className="stat-card">
                        <div className="stat-icon users-icon">👥</div>
                        <div className="stat-content">
                            <h3>{stats.totalUsers.toLocaleString()}</h3>
                            <p>Total Users</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon active-icon">✅</div>
                        <div className="stat-content">
                            <h3>{stats.activeUsers.toLocaleString()}</h3>
                            <p>Active Users</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon logs-icon">📊</div>
                        <div className="stat-content">
                            <h3>{stats.totalAuditLogs.toLocaleString()}</h3>
                            <p>Total Audit Logs</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon today-icon">📈</div>
                        <div className="stat-content">
                            <h3>{stats.todayLogs.toLocaleString()}</h3>
                            <p>Today's Logs</p>
                        </div>
                    </div>
                </div>

                <div className="dashboard-content">
                    <div className="recent-logs-section">
                        <h2>Recent Audit Logs</h2>
                        <div className="logs-table-container">
                            <table className="logs-table">
                                <thead>
                                    <tr>
                                        <th>User ID</th>
                                        <th>Action</th>
                                        <th>Screen</th>
                                        <th>Severity</th>
                                        <th>Timestamp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentLogs.map(log => (
                                        <tr key={log.id}>
                                            <td>{log.userId}</td>
                                            <td>{log.action}</td>
                                            <td>{log.screen}</td>
                                            <td>
                                                <span
                                                    className="severity-badge"
                                                    style={{ backgroundColor: getSeverityColor(log.severity) }}
                                                >
                                                    {log.severity}
                                                </span>
                                            </td>
                                            <td>{formatTimestamp(log.timestamp)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
